import * as Highcharts from "highcharts";
import * as React from "react";
import HighchartsReact from "highcharts-react-official";
import MoreButton, { More, CloseHelpTextButton } from "../common/MoreButton";
import Span from "../../../utils/AnalyticsSpan";
import colors from "../../../../styles/colors";
import { CourseCompletionIcon } from "../AnalyticsIcons";
import { getPercentage } from "../../../../utils/index";
import {
  defaultStyle,
  defaultAxisLabels,
  defaultPlotOptions,
  defaultLegend,
  centerHorizontalBarTooltip,
  defaultTooltip,
} from "../../../../utils/charts";
import { createGradient, defaultBarTooltip } from "../../../../formatters/analytics";
import {
  SectionContainer,
  SectionTitle,
  StatisticsContainer,
  ChartDescriptionContainer,
  ChartTextContainer,
  ChartContainer,
} from "../common/Charts.styles";
import styled from "styled-components";
import { AnimatedUnmountWrapper } from "../../../utils/AnimationUnmountWrapper/AnimationUnmountWrapper";

const CourseChartContainer = styled(ChartContainer as any)<{ isFiltered: boolean }>`
  @media (${({ theme }) => theme.breakpoints.min800}) {
    height: ${props => (props.isFiltered ? "300px" : "250px")};
  }
`;

interface ICompletionChartProps {
  all: number;
  completed: number;
  name: string;
  started: number;
}

interface IFilteredCompletionChartProps extends ICompletionChartProps {
  filteredAll: number;
  filteredCompleted: number;
  filteredStarted: number;
}

interface ICompletationValue {
  amount: number | number[];
  color: string;
  gradientColor: string;
  name: string;
  percentage: number | number[];
}

interface ICompletationData {
  Completed: ICompletationValue;
  "In Progress": ICompletationValue;
  name: string;
  "Not Started": ICompletationValue;
}

enum CompletionCategory {
  Completed = "Completed",
  InProgress = "In Progress",
  NotStarted = "Not Started",
}

const createCompletionGraphSeries = (seriesData: ICompletationValue) => {
  return {
    color: createGradient(seriesData.color, seriesData.gradientColor),
    data: Array.isArray(seriesData.percentage) ? seriesData.percentage.map(y => ({ y })) : [seriesData.percentage],
    name: seriesData.name,
    type: "bar",
  } as Highcharts.SeriesBarOptions;
};

const defaultBarChartOptions = {
  ...defaultPlotOptions,
  series: {
    dataLabels: {
      style: {
        fontSize: "1.4rem",
      },
    },
    stacking: "normal",
  },
};

const options = (data: ICompletationData): Highcharts.Options => {
  const seriesData = [
    data[CompletionCategory.NotStarted],
    data[CompletionCategory.InProgress],
    data[CompletionCategory.Completed],
  ];

  return {
    chart: {
      type: "bar",
    },
    plotOptions: {
      ...defaultBarChartOptions,
    },
    series: seriesData.map(createCompletionGraphSeries),
    title: {
      text: undefined,
    },
    tooltip: {
      formatter() {
        const tooltipAmount = data[this.series.name].amount;
        const categoriesToKeys = { Learners: 0, Filtered: 1 };
        return defaultBarTooltip(
          this.color,
          this.series.name,
          this.y!,
          Array.isArray(tooltipAmount) ? tooltipAmount[categoriesToKeys[this.key as string]] : tooltipAmount
        );
      },
      positioner(...params) {
        return centerHorizontalBarTooltip(this.chart)(...params);
      },
      ...defaultTooltip,
    },
    xAxis: {
      categories: ["Learners"],
      labels: {
        enabled: false,
      },
      visible: false,
    },
    yAxis: {
      max: 100,
      min: 0,
      title: {
        style: {
          fontSize: "1.45rem",
        },
        text: "Percentage",
      },
      ...defaultAxisLabels(),
    },
    ...defaultStyle,
    ...defaultLegend,
  } as Highcharts.Options;
};

const filteredOptions = (data: ICompletationData): Highcharts.Options => {
  return {
    ...options(data),
    chart: {
      /* increase separation of export menu */
      marginTop: 50,
      type: "bar",
      /* tslint:disable-next-line */
      events: {
        load() {
          this.series.forEach(s =>
            s.data.forEach(p => {
              if (p.category === "Learners") {
                p.update({
                  color: createGradient(colors.chartFilteredGray70, colors.chartFilteredGray100),
                });
              }
            })
          );
        },
      },
    },
    xAxis: {
      categories: ["Learners", "Filtered"],
      labels: {
        enabled: false,
      },
      visible: false,
    },
  } as Highcharts.Options;
};

const HorizontalStackedBarChart: React.SFC<ICompletionChartProps> = ({ name, completed, started, all }) => {
  const completedPercentage = getPercentage(completed, all);
  const startedPercentage = getPercentage(started, all);
  const notStartedPercentage = 100 - (completedPercentage + startedPercentage);

  const datum: ICompletationData = {
    Completed: {
      amount: completed,
      color: colors.chartGreen100,
      gradientColor: colors.chartGreen70,
      name: CompletionCategory.Completed,
      percentage: completedPercentage,
    },
    "In Progress": {
      amount: started,
      color: colors.chartDarkBlue70,
      gradientColor: colors.chartDarkBlue100,
      name: CompletionCategory.InProgress,
      percentage: startedPercentage,
    },
    name,
    /* tslint:disable-next-line */
    "Not Started": {
      amount: all - completed - started,
      color: colors.chartLightGrey100,
      gradientColor: colors.chartLightGrey70,
      name: CompletionCategory.NotStarted,
      percentage: notStartedPercentage,
    },
  };

  return (
    <HighchartsReact
      containerProps={{ style: { width: "100%", height: "100%", display: "block" } }}
      highcharts={Highcharts}
      options={options(datum)}
    />
  );
};

const FilteredBarChart: React.SFC<IFilteredCompletionChartProps> = ({
  name,
  completed,
  started,
  all,
  filteredAll,
  filteredCompleted,
  filteredStarted,
}) => {
  const completedAmount = [completed, filteredCompleted];
  const allAmount = [all, filteredAll];
  const startedAmount = [started, filteredStarted];
  const notStartedAmount = [all - completed - started, filteredAll - filteredCompleted - filteredStarted];

  const completedPercentage = completedAmount.map((x, i) => getPercentage(x, allAmount[i]));
  const startedPercentage = startedAmount.map((x, i) => getPercentage(x, allAmount[i]));
  const notStartedPercentage = completedPercentage.map((x, i) => 100 - (x + startedPercentage[i]));

  const datum: ICompletationData = {
    Completed: {
      amount: completedAmount,
      color: colors.chartGreen100,
      gradientColor: colors.chartGreen70,
      name: CompletionCategory.Completed,
      percentage: completedPercentage,
    },
    "In Progress": {
      amount: startedAmount,
      color: colors.chartDarkBlue70,
      gradientColor: colors.chartDarkBlue100,
      name: CompletionCategory.InProgress,
      percentage: startedPercentage,
    },
    name,
    /* tslint:disable-next-line */
    "Not Started": {
      amount: notStartedAmount,
      color: colors.chartLightGrey100,
      gradientColor: colors.chartLightGrey70,
      name: CompletionCategory.NotStarted,
      percentage: notStartedPercentage,
    },
  };

  return (
    <HighchartsReact
      containerProps={{ style: { width: "100%", height: "100%", display: "block" } }}
      highcharts={Highcharts}
      options={filteredOptions(datum)}
    />
  );
};

export interface ICourseCompletionGraphProps {
  useSelectedAsFilter: boolean;
  allCount: number;
  allStartedCount: number;
  allCompletedCount: number;
  selectedCount: number;
  selectedStartedCount: number;
  selectedCompletedCount: number;
}

export interface ICourseCompletionGraphState {
  showMore: boolean;
}

export default class CourseCompletionGraph extends React.PureComponent<
  ICourseCompletionGraphProps,
  ICourseCompletionGraphState
> {
  constructor(props: ICourseCompletionGraphProps) {
    super(props);
    this.state = { showMore: false };
  }

  public render() {
    const {
      useSelectedAsFilter,
      allCount,
      allStartedCount,
      allCompletedCount,
      selectedCount,
      selectedStartedCount,
      selectedCompletedCount,
    } = this.props;

    return (
      <SectionContainer>
        <StatisticsContainer>
          <ChartTextContainer>
            <SectionTitle data-testid="course-completion-title">
              <CourseCompletionIcon /> Course Completion
            </SectionTitle>
            <ChartDescriptionContainer data-testid="course-completion-description">
              <div>
                So far, <Span color={colors.chartGreen100}>{getPercentage(allCompletedCount, allCount)}%</Span> of the
                expected <Span color={colors.chartDarkBlue100}>{allCount}</Span> Learners have completed the course and
                a further <Span color={colors.chartDarkBlue100}>{getPercentage(allStartedCount, allCount)}%</Span> are
                In Progress.
              </div>
              <MoreButton onClick={this.onToggleShowMore} isOpenText={this.state.showMore} />
            </ChartDescriptionContainer>
          </ChartTextContainer>
          {selectedCount > 0 && useSelectedAsFilter ? (
            <CourseChartContainer isFiltered={true} data-testid="course-completion-filtered">
              <FilteredBarChart
                name={"Filtered"}
                completed={allCompletedCount}
                started={allStartedCount}
                all={allCount}
                filteredAll={selectedCount}
                filteredCompleted={selectedCompletedCount}
                filteredStarted={selectedStartedCount}
              />
            </CourseChartContainer>
          ) : (
            <CourseChartContainer isFiltered={false} data-testid="course-completion-unfiltered">
              <HorizontalStackedBarChart
                name={"All"}
                completed={allCompletedCount}
                started={allStartedCount}
                all={allCount}
              />
            </CourseChartContainer>
          )}
        </StatisticsContainer>
        <AnimatedUnmountWrapper isMounted={this.state.showMore} delayTime={300}>
          <More>
            <CloseHelpTextButton onClick={this.onToggleShowMore} isOpenText={this.state.showMore} />
            <p>
              The Course Completion chart is the only chart that shows all Learners. All other charts describe only the
              Learners who have already completed the entire course.
            </p>
            <p>
              This chart shows the overall progress of the course. The green segment indicates the proportion of
              Learners that have already completed the course, the dark blue segment indicates the Learners who are In
              Progress (i.e. have started, but not completed) and the grey area indicates Learners who are expected to
              take the course, but have not yet started.
            </p>
            <p>
              The segments of this chart can be clicked to filter the corresponding sets of Learners. For example,
              clicking the dark blue segment would filter and present all Learners who have started, but not yet
              finished the course.
            </p>
          </More>
        </AnimatedUnmountWrapper>
      </SectionContainer>
    );
  }

  public onToggleShowMore = () => {
    this.setState({ showMore: !this.state.showMore });
  };
}
